import React from "react";
import { Link } from "@inertiajs/react";
import {
  Box,
  Stack,
  Group,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { IconArrowUpRight } from "@tabler/icons-react";
import { Card } from "./UI/Card";
import { Heading4, Eyebrow, Caption } from "./UI/Typography";
import type { Pillar } from "../data/pillars";

// Mirrors PillarCard's hexToRgba glow tinting.
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

interface PillarChipProps {
  pillar: Pillar;
}

/**
 * PillarChip — compact mobile-only variant of PillarCard.
 * Renders in a 2×2 grid above the fold as the primary discovery affordance.
 * Full PillarCard (with body copy + destination-specific CTA) still renders
 * at ≥ sm breakpoints via the standard Four Disciplines section.
 */
export const PillarChip: React.FC<PillarChipProps> = ({ pillar }) => {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;
  const accentColor = other[pillar.accentColor];
  const accentTextColor = other[pillar.accentTextColor];
  const [hovered, setHovered] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);

  const glassHover = {
    transform: "translateY(-4px)",
    boxShadow: `0 16px 40px ${hexToRgba(accentColor, 0.22)}, 0 0 0 1px ${hexToRgba(accentColor, 0.28)}, inset 0 1px 0 rgba(255,255,255,0.1)`,
    border: `1px solid ${hexToRgba(accentColor, 0.28)}`,
  };

  const glassRest = {
    transform: "translateY(0)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.07), 0 6px 20px rgba(0,0,0,0.4)",
    border: `1px solid ${hexToRgba(accentColor, 0.12)}`,
  };

  const glassActive = {
    transform: "translateY(-2px) scale(0.99)",
    boxShadow: `0 8px 20px ${hexToRgba(accentColor, 0.18)}, 0 0 0 1px ${hexToRgba(accentColor, 0.24)}, inset 0 1px 0 rgba(255,255,255,0.08)`,
    border: `1px solid ${hexToRgba(accentColor, 0.24)}`,
  };

  return (
    <UnstyledButton
      component={Link}
      href={pillar.href}
      style={{
        textDecoration: "none",
        display: "block",
        height: "100%",
        width: "100%",
      }}
    >
      <Card
        padding="md"
        radius="md"
        h="100%"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setPressed(false);
        }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        style={{
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: "pointer",
          ...(pressed ? glassActive : hovered ? glassHover : glassRest),
        }}
      >
        <Stack gap="xs" h="100%">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Box
              style={{
                width: 32,
                height: 32,
                borderRadius: "var(--mantine-radius-sm)",
                background: `linear-gradient(135deg, ${accentColor}33, ${accentColor}11)`,
                border: `1px solid ${accentColor}33`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <pillar.icon size={18} style={{ color: accentColor }} />
            </Box>
            <IconArrowUpRight
              size={16}
              style={{ color: accentTextColor, flexShrink: 0 }}
            />
          </Group>

          <Stack gap={2} mt="xs">
            <Eyebrow accent={accentTextColor}>{pillar.label}</Eyebrow>
            <Heading4 style={{ margin: 0 }}>{pillar.heading}</Heading4>
          </Stack>

          <Caption style={{ marginTop: "auto" }}>{pillar.meta}</Caption>
        </Stack>
      </Card>
    </UnstyledButton>
  );
};
